import React from 'react';
import { PASSWORD_REQUIREMENTS } from '@shared/schemas/password.schema.js';
import { FaCheckCircle, FaRegCircle } from 'react-icons/fa';

// PASSWORD_REQUIREMENTS comes from shared and is the same list the backend's
// PasswordSchema validates against, so the checklist cannot drift from the
// server's rules. The rows render in a two-column grid; an odd trailing row
// spans both columns.
const isFullWidthRow = (index: number) =>
  index === PASSWORD_REQUIREMENTS.length - 1 && PASSWORD_REQUIREMENTS.length % 2 === 1;

interface PasswordRequirementsChecklistProps {
  /** Point the password input's `aria-describedby` here. */
  id: string;
  password: string;
}

/** The live "Password must contain" panel under a new-password field. */
export const PasswordRequirementsChecklist: React.FC<PasswordRequirementsChecklistProps> = ({ id, password }) => {
  const metRequirementCount = PASSWORD_REQUIREMENTS.filter(requirement => requirement.test(password)).length;
  const allRequirementsMet = metRequirementCount === PASSWORD_REQUIREMENTS.length;

  return (
    <>
      {/* Password Requirements - updates live as the user types.
          The panel is the field's description (read on focus). Progress is
          announced through a single status region so screen readers hear
          "n of 5 requirements met" instead of fragments from every row. */}
      <div
        id={id}
        className="mt-2 p-3 bg-game-accent/10 border border-game-accent/30 rounded-md"
      >
        <div className="flex items-baseline justify-between gap-2 mb-2">
          <p className="text-xs text-white/80 font-medium">Password must contain:</p>
          {password && (
            <p
              aria-hidden="true"
              className={`text-xs font-medium ${allRequirementsMet ? 'text-green-300' : 'text-white/60'}`}
            >
              {metRequirementCount}/{PASSWORD_REQUIREMENTS.length}
            </p>
          )}
        </div>
        <ul className="grid grid-cols-2 gap-1 text-xs list-none p-0 m-0">
          {PASSWORD_REQUIREMENTS.map((requirement, index) => {
            const isMet = requirement.test(password);
            return (
              <li
                key={requirement.id}
                className={`flex items-center gap-1 ${isFullWidthRow(index) ? 'col-span-2' : ''}`}
              >
                {isMet ? (
                  <FaCheckCircle className="text-green-400 flex-shrink-0" aria-hidden="true" />
                ) : (
                  <FaRegCircle className="text-white/40 flex-shrink-0" aria-hidden="true" />
                )}
                <span className={`transition-colors duration-200 ${isMet ? 'text-green-200' : 'text-white/60'}`}>
                  {requirement.label}
                </span>
                <span className="sr-only">{isMet ? '(met)' : '(not met yet)'}</span>
              </li>
            );
          })}
        </ul>
      </div>
      {/* Always mounted so assistive tech registers the region before it first changes */}
      <span role="status" className="sr-only">
        {password
          ? `${metRequirementCount} of ${PASSWORD_REQUIREMENTS.length} requirements met`
          : ''}
      </span>
    </>
  );
};
